import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { ArrowRight, Loader2 } from 'lucide-react';
import { promptRefinementService, type RefinementSession, type RunErrorAnalysis, type CandidatePromptResponse, type CandidateSchemaResponse } from '../../../services/promptRefinementService';
import {
  FieldSuggestionCard,
  type FieldSuggestion,
  type FieldDecisions,
  type SuggestionDecision,
  type SuggestionItem,
} from './FieldSuggestionCard';

interface Props {
  session: RefinementSession | null;
  errorAnalysis: RunErrorAnalysis | null;
  onComplete: (session: RefinementSession, candidate: CandidatePromptResponse | CandidateSchemaResponse | null, candidateSchema?: CandidateSchemaResponse | null) => void;
}

export function StepSuggestions({ session, errorAnalysis, onComplete }: Props) {
  const [allDecisions, setAllDecisions] = useState<Record<string, FieldDecisions>>({});
  const [applying, setApplying] = useState(false);

  // Parse suggestions from session
  const fieldSuggestions: FieldSuggestion[] = useMemo(() => {
    if (!session?.suggestions) return [];

    const raw = session.suggestions as Record<string, unknown>;
    const fields: FieldSuggestion[] = [];

    // The API returns { fields: [...] } where each entry has field_name + suggestions array
    const fieldEntries: Array<Record<string, unknown>> = Array.isArray(raw.fields)
      ? (raw.fields as Array<Record<string, unknown>>)
      : Object.entries(raw)
          .filter(([key]) => key !== 'fields')
          .map(([key, val]) => ({ field_name: key, ...(val as Record<string, unknown>) }));

    for (const fd of fieldEntries) {
      const fieldName = fd.field_name as string;
      if (!fieldName) continue;

      const items: SuggestionItem[] = [];

      // Handle suggestions array format: { suggestions: [{ sub_field, current_value, proposed_value, ... }] }
      if (Array.isArray(fd.suggestions)) {
        for (const s of fd.suggestions as Array<Record<string, unknown>>) {
          items.push({
            sub_field: (s.sub_field as string) || 'notes',
            rationale: (s.rationale as string) || (s.description as string) || '',
            current_value: (s.current_value as string) || (s.current_text as string) || '',
            proposed_value: (s.proposed_value as string) || (s.proposed_text as string) || '',
          });
        }
      }

      if (items.length > 0) {
        const fieldAnalysis = errorAnalysis?.fields.find(f => f.field_name === fieldName);
        fields.push({
          field_name: fieldName,
          error_rate: fieldAnalysis?.error_rate || (fd.error_rate as number) || 0,
          dominant_error_type: fieldAnalysis?.dominant_error_type || (fd.dominant_error_type as string) || 'unknown',
          suggestions: items,
        });
      }
    }

    return fields.sort((a, b) => b.error_rate - a.error_rate);
  }, [session, errorAnalysis]);

  // Restore accepted_suggestions from session by matching against suggestions
  useEffect(() => {
    if (!session?.accepted_suggestions || !session?.suggestions || Object.keys(allDecisions).length > 0) return;

    const accepted = session.accepted_suggestions as Record<string, unknown>;
    const acceptedFields = Array.isArray(accepted.fields) ? accepted.fields as Array<Record<string, unknown>> : [];
    if (acceptedFields.length === 0) return;

    // Build a set of accepted (field_name, sub_field, proposed_value) tuples
    const acceptedSet = new Set<string>();
    for (const af of acceptedFields) {
      const fieldName = af.field_name as string;
      const suggestions = Array.isArray(af.suggestions) ? af.suggestions as Array<Record<string, unknown>> : [];
      for (const s of suggestions) {
        acceptedSet.add(`${fieldName}|${s.sub_field}|${s.proposed_value}`);
      }
    }

    // Match against fieldSuggestions to restore decisions
    const restored: Record<string, FieldDecisions> = {};
    for (const field of fieldSuggestions) {
      const decisions: FieldDecisions = {};
      for (let i = 0; i < field.suggestions.length; i++) {
        const sugg = field.suggestions[i];
        const key = `${field.field_name}|${sugg.sub_field}|${sugg.proposed_value}`;
        decisions[i] = {
          decision: acceptedSet.has(key) ? 'accepted' : 'rejected',
        };
      }
      restored[field.field_name] = decisions;
    }
    setAllDecisions(restored);
  }, [session, fieldSuggestions]);

  const handleDecisionChange = (
    fieldName: string,
    suggestionIndex: number,
    decision: SuggestionDecision,
    editedText?: string
  ) => {
    setAllDecisions(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [suggestionIndex]: { decision, edited_text: editedText },
      },
    }));
  };

  // Count accepted
  const totalSuggestions = fieldSuggestions.reduce((sum, f) => sum + f.suggestions.length, 0);
  const acceptedCount = Object.values(allDecisions).reduce((sum, fieldDec) => {
    return sum + Object.values(fieldDec).filter(d => d.decision === 'accepted').length;
  }, 0);

  const handleApply = async () => {
    if (!session) return;
    try {
      setApplying(true);

      // Build accepted_suggestions in the format expected by apply_and_clone:
      // { "fields": [{ "field_name": "...", "suggestions": [{ "sub_field": "...", ... }] }] }
      const acceptedFields: Array<{ field_name: string; suggestions: Array<Record<string, unknown>> }> = [];

      for (const field of fieldSuggestions) {
        const fieldDec = allDecisions[field.field_name];
        if (!fieldDec) continue;

        const acceptedSuggs: Array<Record<string, unknown>> = [];
        for (const [idxStr, decision] of Object.entries(fieldDec)) {
          if (decision.decision !== 'accepted') continue;

          const idx = parseInt(idxStr, 10);
          const originalSugg = field.suggestions[idx];
          if (!originalSugg) continue;

          const sugg: Record<string, unknown> = {
            sub_field: originalSugg.sub_field,
            target: 'schema',
            rationale: originalSugg.rationale,
            current_value: originalSugg.current_value || '',
            proposed_value: decision.edited_text ?? originalSugg.proposed_value ?? '',
          };

          acceptedSuggs.push(sugg);
        }

        if (acceptedSuggs.length > 0) {
          acceptedFields.push({
            field_name: field.field_name,
            suggestions: acceptedSuggs,
          });
        }
      }

      const acceptedPayload = { fields: acceptedFields };

      // Patch suggestions
      const updatedSession = await promptRefinementService.patchSuggestions(session.id, acceptedPayload);

      // Apply: schema or prompt based on suggestion targets
      const hasSchemaTargets = acceptedFields.some(f =>
        f.suggestions.some(s => s.target === 'schema')
      );
      const hasPromptTargets = acceptedFields.some(f =>
        f.suggestions.some(s => s.target === 'prompt')
      );

      let candidatePrompt: CandidatePromptResponse | null = null;
      let candidateSchema: CandidateSchemaResponse | null = null;

      if (hasSchemaTargets) {
        candidateSchema = await promptRefinementService.applySchema(updatedSession.id);
      }
      if (hasPromptTargets) {
        candidatePrompt = await promptRefinementService.apply(updatedSession.id);
      }

      const label = [hasSchemaTargets && 'Schéma', hasPromptTargets && 'Prompt'].filter(Boolean).join(' + ');
      toast.success(`${label} candidat(s) généré(s) avec succès`);

      // Reload session to get updated candidate IDs
      const refreshedSession = await promptRefinementService.getSession(updatedSession.id);
      onComplete(refreshedSession, candidatePrompt, candidateSchema);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'application');
    } finally {
      setApplying(false);
    }
  };

  if (!session) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800">Veuillez d'abord effectuer l'analyse dans l'étape précédente.</p>
      </div>
    );
  }

  if (fieldSuggestions.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">Aucune suggestion disponible pour cette session.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-sm text-gray-500">Champs analysés</span>
              <p className="text-xl font-bold text-gray-900">{fieldSuggestions.length}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Suggestions générées</span>
              <p className="text-xl font-bold text-gray-900">{totalSuggestions}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Suggestions acceptées</span>
              <p className="text-xl font-bold text-green-600">{acceptedCount}/{totalSuggestions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Field suggestion cards */}
      {fieldSuggestions.map(field => (
        <FieldSuggestionCard
          key={field.field_name}
          field={field}
          decisions={allDecisions[field.field_name] || {}}
          onDecisionChange={handleDecisionChange}
        />
      ))}

      {/* Apply button */}
      <div className="flex justify-end">
        <button
          onClick={handleApply}
          disabled={applying || acceptedCount === 0}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {applying ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ArrowRight size={18} />
          )}
          Générer le prompt candidat
        </button>
      </div>
    </div>
  );
}
